// Shared provider resolution; both damage lanes keep their existing fragment/total arithmetic.
import { type JanusCtx, type Row, activeStatuses, ecId, handBack, itemField, record, scalar, upper, definitionNotHeld } from '../context.js';
import { statusItemId } from '../../statuses/expiry.js';
import { passiveEntryActive, statusItemPassiveEntries } from './status-item-passive-entries.js';
import { readLayerListEntryUuid } from '../../compile/inventory-raw-readers.js';
import { isAuraPassive } from '../../compile/passive-integer-fold.js';
import { AuraOwnerGateUnavailable, isDormantOwnerGatedAura, isHostileAuraSelfAnchor } from '../../compile/creature-status-reader.js';
import { answerConditionContext, ConditionContextUnknown, type ConditionScene } from '../../compile/condition-context.js';
import { createAbilityConditionEvaluator, isAbilityConditionLayer } from '../../compile/ability-conditions.js';
import { C1_CONTRACT } from '../../compile/possessor-compile.js';
import { findInventoryRow } from '../../inventory/flag-adjust.js';

export const ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION = 'attacks_ignore_natural_damage_mitigation';
const phpTruthy = (value: unknown): boolean => { const x = scalar(value); return !(x == null || x === '' || x === '0' || x === 0 || x === false); };

/** Readers shared by ability damage and the weapon planner; no bundle-local state. */
export interface DamageProviderReaders {
  value: (row: Row, name: string) => unknown;
  entries: (row: Row, name: string) => unknown[];
  definitionOn: (row: Row, id: string) => Row | null;
  statusRows: (row: Row) => Row[];
  member?: (id: string) => Row | undefined;
  conditionScene?: () => Pick<ConditionScene, 'encounter' | 'campaign'> | null;
  charges?: ConditionScene['charges'];
  lightTypesPresent?: (holder: Row) => string[] | null;
}

export const DAMAGE_PROVIDER_LAYERS: ReadonlySet<string> = new Set([
  ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION, 'damage_ignores_all_damage_mitigation', 'attacks_ignore_damage_mitigation',
  'spells_ignore_damage_mitigation', 'ignore_damage_mitigation_damage_type', 'damage_to_objects_multiplier',
]);
class DamageProviderGap extends Error { constructor(readonly reason: string) { super(reason); } }

/** Resolve damage providers once, before draws. Passive stamps are authoritative; unstamped passives and feats use the native
 * condition evaluator. Timed bucket copies need their held window; aura payload comes only from a live recipient status. */
export function damageProviderDefinitions(read: DamageProviderReaders, holder: Row, item: Row | null,
  layers: ReadonlySet<string> = DAMAGE_PROVIDER_LAYERS, skipMissingStatus?: () => void): Row[] | string {
  const relevant = (def: Row): boolean => [...layers].some(key => damageProviderFlag(read, [def], key));
  const layered = (v: unknown): Row | null => { const row = record(v); return row && Object.keys(record(row.item_layer_types) ?? {}).length ? row : null; };
  const entryId = (raw: unknown): string => {
    const row = record(raw);
    return upper(scalar(row?.ability_id) ?? (typeof row?.item_arr === 'string' ? row.item_arr : null)
      ?? itemField(record(row?.item_arr), 'item_id') ?? readLayerListEntryUuid(raw));
  };
  const prefix = layers.size === 1 && layers.has(ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION) ? 'natural-mitigation-ignore' : 'mitigation-ignore';
  const gap = (reason: string): never => { throw new DamageProviderGap(`${prefix}-${reason}`); };
  const resolve = (raw: unknown, role: string): Row => layered(record(raw)?.item_arr) ?? layered(raw)
    ?? read.definitionOn(holder, entryId(raw)) ?? gap(`${role}-definition-not-held:${entryId(raw)}`);
  const out = [item, record(item?.attack_ability_arr), holder].filter((row): row is Row => !!row);
  if (!layers.size) return out;
  // Boolean-only callers (including natural-base prefetch) already have their answer from a direct provider.
  const only = [...layers][0]!;
  if (layers.size === 1 && only !== 'ignore_damage_mitigation_damage_type' && only !== 'damage_to_objects_multiplier'
      && damageProviderFlag(read, out, only)) return out;
  const statuses = read.statusRows(holder);
  const host = { ...holder, statuses: { ...record(holder.statuses), target: statuses } };
  const heldItems = new Set(statuses.map(statusItemId));
  let conditions: ReturnType<typeof createAbilityConditionEvaluator> | undefined, conditionRole = '';
  const conditionKeys = (def: Row, role: string, aura = false): void => {
    for (const key of [...Object.keys(def), ...Object.keys(record(def.item_layer_types) ?? {})])
      if (key.startsWith('if_') && !isAbilityConditionLayer(key) && !(aura && key === 'if_aura_owner_has_status_effect_active')
          && read.value(def, key) != null) gap(`${role}-condition-not-modelled:${key}`);
  };
  const met = (def: Row, role: string): boolean => {
    conditionRole = role;
    conditionKeys(def, role);
    conditions ??= createAbilityConditionEvaluator({ catalogRows: C1_CONTRACT.catalogRows, abilityTypes: C1_CONTRACT.abilityTypes,
      layerReaders: { layerValue: (row, key) => row ? read.value(row as Row, key) ?? null : null,
        layerEntries: (row, key) => row ? read.entries(row as Row, key) : [] },
      aggregationStrategy: name => C1_CONTRACT.catalogRows[name]?.aggregation_strategy ?? 'host_wins',
      resolveCarrierRef: id => read.definitionOn(holder, upper(id)) ?? gap(`${conditionRole}-definition-not-held:${upper(id)}`),
      resolveInventoryInstance: id => findInventoryRow(host, id) ?? gap(`${conditionRole}-condition-inventory-not-held:${upper(id)}`),
      queryTemplateDefinition: id => read.definitionOn(holder, upper(id)) ?? gap(`${conditionRole}-definition-not-held:${upper(id)}`),
      equipmentSlots: C1_CONTRACT.equipmentSlots,
      vitalityStatusIds: C1_CONTRACT.vitalityStatusIds, skillEntries: C1_CONTRACT.skillEntries,
      conditionSource: (kind, args) => {
        if (kind === 'present-light' && read.lightTypesPresent) return read.lightTypesPresent(host);
        try { return answerConditionContext(kind, args, { ...(read.conditionScene?.() ?? { encounter: record(holder.encounter_arr), campaign: undefined }),
          value: (row, key) => row ? read.value(row, key) : null, entries: (row, key) => row ? read.entries(row, key) : [],
          abilityTypes: C1_CONTRACT.abilityTypes, equipmentSlots: C1_CONTRACT.equipmentSlots,
          definition: id => read.definitionOn(holder, id), charges: read.charges }); }
        catch (error) { if (error instanceof ConditionContextUnknown) gap(`${conditionRole}-condition-context-not-held:${kind}`); throw error; }
      } });
    return conditions.check(def, host, false, false);
  };
  try {
    for (const raw of read.entries(holder, 'passive_ability')) {
      if (!passiveEntryActive(raw)) continue;
      const def = resolve(raw, 'passive');
      if (!relevant(def) || isAuraPassive(def, (row, key) => row ? read.value(row as Row, key) : null)) continue;
      if (read.value(def, 'triggers_after_combat_begins') != null && read.value(def, 'duration_type') != null
          && !heldItems.has(upper(itemField(def, 'item_id')))) continue;
      if (!('passive_conditions_active' in (record(raw) ?? {})) && !met(def, 'passive')) continue;
      out.push(def);
    }
    for (const status of statuses) {
      const def = layered(status.status_arr) ?? read.definitionOn(holder, statusItemId(status));
      if (!def) {
        if (skipMissingStatus) skipMissingStatus();
        else gap(`status-definition-not-held:${statusItemId(status)}`);
        continue;
      }
      if (!relevant(def)) continue;
      if (String(itemField(def, 'item_type') ?? '') === 'passive_ability') {
        conditionKeys(def, 'status-passive', true);
        // The shared status-passive reader owns team, conditions and aura payload. Bucket masking is unnecessary here:
        // flags/types/maxima are idempotent, and an aura bucket deliberately provided nothing above.
        const payload = statusItemPassiveEntries(host, [{ status, definition: def }], { ...read,
          entries: (row, key) => row === host && key === 'passive_ability' ? [] : read.entries(row, key),
          definition: id => read.definitionOn(holder, id), member: id => read.member?.(id), relevant });
        if ('gap' in payload) return `${prefix}-${payload.gap}`;
        out.push(...payload.entries.map(entry => entry.definition));
        continue;
      }
      const target = status.status_target_id ?? status.target ?? ecId(holder);
      const caster = 'status_caster_id' in status ? status.status_caster_id : status.caster === undefined ? target : status.caster;
      const value = (row: unknown, key: string): unknown => record(row) ? read.value(row as Row, key) : null;
      if (isHostileAuraSelfAnchor(def, status.status_aoe_source, caster, target, value)) continue;
      if (isDormantOwnerGatedAura({ ...status, status_arr: def, status_caster_id: caster }, value,
        id => read.member?.(upper(id)) ?? (upper(id) === ecId(holder) ? holder : null), holder)) continue;
      out.push(def);
    }
    for (const raw of read.entries(holder, 'feat')) {
      const def = resolve(raw, 'feat');
      if (relevant(def) && met(def, 'feat')) out.push(def);
    }
  } catch (error) {
    if (error instanceof DamageProviderGap) return error.reason;
    if (error instanceof AuraOwnerGateUnavailable) return `${prefix}-${error.reason}`;
    throw error;
  }
  return out;
}

/** The flag uses the page's layer reader, including wrapped/omitted fields and compiled slots. */
export function damageProviderFlag(read: Pick<DamageProviderReaders, 'value'>, providers: readonly Row[], layer: string): boolean {
  // A finalized ROOT union may coexist with a host-authored bucket slot. Read both through the page reader.
  return providers.some(row => phpTruthy(read.value(row, layer)) || phpTruthy(read.value({ ...row, item_layer_types: undefined }, layer)));
}

export function ctxDamageProviderReaders(ctx: JanusCtx, holder: Row): DamageProviderReaders {
  return { value: ctx.read.value, entries: ctx.read.entries, definitionOn: (_row, id) => ctx.read.definition(id),
    statusRows: row => activeStatuses(ctx, row), member: id => ctx.members.get(id)
      ?? Object.values((ctx.encounter.encounter_creatures_arr ?? {}) as object).map(record).find(row => ecId(row) === id) ?? undefined,
    conditionScene: () => ctx.read.possessorScene?.(holder, [...ctx.members.values()])?.conditionScene?.()
      ?? { encounter: ctx.encounter, campaign: undefined },
    ...(ctx.read.lightTypesPresent ? { lightTypesPresent: row => ctx.read.lightTypesPresent!(row, [...ctx.members.values()]) } : {}) };
}

export function ctxDamageProviders(ctx: JanusCtx, holder: Row, item: Row | null, layers = DAMAGE_PROVIDER_LAYERS): Row[] {
  const providers = damageProviderDefinitions(ctxDamageProviderReaders(ctx, holder), holder, item, layers);
  if (typeof providers === 'string') {
    const id = /definition-not-held:([0-9A-F-]{36})$/.exec(providers)?.[1];
    if (id) definitionNotHeld(`damage_type_roll:${providers.slice(0, -37)}`, id);
    handBack(`damage_type_roll:${providers}`);
  }
  return providers;
}

export const IGNORE_DAMAGE_MITIGATION_DAMAGE_TYPE = 'ignore_damage_mitigation_damage_type';

/** The Damage Mitigation actually subtracted from `total` when `exempt` of it came from exempt damage types. */
export function mitigationAfterTypedIgnore(mitigation: number, total: number, exempt: number): number {
  if (!(exempt > 0)) return mitigation;
  return Math.max(0, Math.min(mitigation, total - exempt));
}

/** Union of eligible providers' types, read through the same page readers as damage planning. */
export function ignoredMitigationTypes(read: { entries: (row: Row, key: string) => unknown[] }, providers: readonly Row[]): string[] {
  return [...new Set(providers.flatMap(row => [...read.entries(row, IGNORE_DAMAGE_MITIGATION_DAMAGE_TYPE),
    ...read.entries({ ...row, item_layer_types: undefined }, IGNORE_DAMAGE_MITIGATION_DAMAGE_TYPE)])
    .map(entry => upper(readLayerListEntryUuid(entry))).filter(Boolean))];
}
